import Box from '@mui/material/Box';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { Link, NavLink, withRouter } from 'react-router-dom';

import Searchbar from '../../../components/Searchbar';
import SpringLoader from '../../../components/SpringLoader/SpringLoader.js';
import { config } from '../../../config';
import { GET, newFetch, POST } from '../../../helpers/fetch';

const { SERVER_URL } = config;

const Esign = ({ history }) => {
  const clientID = parseInt(localStorage.getItem('client'), 10);
  const [esignDocuments, setEsignDocuments] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [statusOptions, setStatusOptions] = useState([]);
  const [filterName, setFilterName] = useState('');

  const [sortModel, setSortModel] = useState([
    { field: 'id', sort: 'asc' }, // Initial sort field and order
  ]);
  const [clients, setClients] = useState([]);

  const defaultFontColor = '#000000';

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="row">
            <Link
              className="text-primary "
              to={`/esign_document/edit/${params.id}`}
            >
              <div className="col-md-12 adjust-fs text-muted">{params.id}</div>
            </Link>
          </div>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <span
            className="adjust-fs text-muted"
            style={{ color: '#212529', fontSize: 15, fontWeight: 400 }}
          >
            {params.row.name || '--'}
          </span>
        );
      },
    },
    {
      field: 'nric_fin',
      headerName: 'NRIC',
      flex: 1,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <span
            className="adjust-fs text-muted"
            style={{ color: '#212529', fontSize: 15, fontWeight: 400 }}
          >
            {params.row.nric_fin
              ? params.row.nric_fin.replace(/.(?=.{4})/g, 'X')
              : '-'}
          </span>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      resizable: false,
      filterable: true,
      renderCell: (params) => {
        return (
          <div
            className={`badge ${
              params.row.status == 'Pending'
                ? 'bg-warning'
                : params.row.status == 'Completed'
                ? 'bg-primary-custom'
                : 'bg-dark'
            } adjust-fs adjust-shadow fw-normal`}
            style={{
              fontSize: 14,
              color: '#fff',
              fontWeight: 200,
              padding: '6px 17px',
            }}
          >
            {params.row.status}
          </div>
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Date Modified',
      flex: 1,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        const date = new Date(params.row.updatedAt);

        if (!params.row.updatedAt) return '--';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}`;

        return (
          <span className="adjust-fs text-muted fw-normal">
            {formattedDate}
          </span>
        );
      },
    },
  ];

  const [data, setData] = React.useState([]);

  const fetchEsignDocuments = async (pagination, field, sort, filterName) => {
    setLoading(true);
    let api_path = `/esign_document/list/filter`;
    let response = await newFetch(POST, api_path, {
      field,
      sort,
      page: pagination.page,
      pageSize: pagination.pageSize,
      clientID,
      filterName,
    });

    if (response.success) {
      const eSignDocumentData = response.data.list;

      setEsignDocuments(eSignDocumentData);
      setTotalRows(response.data.total_record);
    }
    setLoading(false);
  };

  const fetchStatusOptions = async () => {
    let api_path = `/fields/esign_document_status`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      setStatusOptions(response.data);
    }
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const searchHandler = (e) => {
    setFilterName(e.target.value);
  };

  React.useLayoutEffect(() => {
    setData(esignDocuments !== undefined ? esignDocuments : []);

    esignDocuments !== undefined && setLoading(false);
  }, [esignDocuments]);

  useEffect(() => {
    fetchStatusOptions();
  }, []);

  useEffect(() => {
    const { field, sort } = sortModel[0] || {};
    fetchEsignDocuments(paginationModel, field, sort, filterName);
  }, [sortModel, paginationModel, clientID, filterName]);

  return (
    <>
      <div
        className="container-fluid"
        style={{
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingBottom: '6rem',
        }}
      >
        <div className="border-bottom pb-2 py-2 d-flex align-items-center">
          <h6 className="flex-grow-1 pt-4" style={{ color: '#A6478E' }}>
            Documents
          </h6>
          <NavLink
            to="/esign_document/add"
            className="text-decoration-none"
            style={{ color: 'inherit' }}
          >
            <button
              className="btn btn-outline-brand ms-3 mt-2 adjust-fs adjust-shadow"
              style={{ width: '200px' }}
            >
              Add New
            </button>
          </NavLink>
        </div>
        <>
          <Searchbar
            search={(e) => searchHandler(e)}
            width="100%"
            placeholder="Search for name or nric"
          />
          <div className="d-flex flex-row align-items-center justify-content-between py-3">
            <h6 className="flex-grow-1">Documents ({totalRows})</h6>
          </div>
          <div className="table-responsive rounded shadow bg-white">
            {loading ? (
              <div
                className="springloader-holder"
                style={{ position: 'relative' }}
              >
                <SpringLoader />
              </div>
            ) : (
              <Box sx={{ width: '100%', fontSize: 16 }}>
                <DataGrid
                  columns={columns}
                  rows={data}
                  rowCount={totalRows}
                  loading={loading}
                  pageSizeOptions={[10, 20, 30, 40, 50, 80, 100]}
                  paginationModel={paginationModel}
                  paginationMode="server"
                  onPaginationModelChange={setPaginationModel}
                  localeText={{
                    noRowsLabel: 'No records found.',
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 10 },
                    },
                  }}
                  style={{ minHeight: 100 }}
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  getRowHeight={() => 'auto'}
                  sx={{
                    [`& .${gridClasses.cell}`]: {
                      py: 3,
                    },
                    [`& .${gridClasses.columnHeader}`]: {
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#6c757d!important',
                    },
                    [`& .${gridClasses.row}`]: {
                      fontSize: '14px',
                      color: '#A6478E!important',
                      fontWeight: 700,
                    },
                  }}
                  disableSelectionOnClick
                  isRowSelectable={() => false}
                />
              </Box>
            )}
          </div>
        </>
      </div>
    </>
  );
};

export default withRouter(Esign);
